import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Shield, ShieldCheck, ShieldAlert, ShieldX, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

import { formatDate } from '../utils/timeFormat';
/**
 * Iter-212 — Background Check visibility surface for talent.
 *
 * The Yardstik integration was already wired on the backend (endpoints:
 *   GET    /api/background-checks/my-status
 *   GET    /api/background-checks/requirements
 *   POST   /api/background-checks/initiate
 * ), but no talent-facing UI existed, so talent couldn't tell whether a
 * check was required, in flight, or expiring. This card surfaces:
 *
 *   • current status (with traffic-light badge + icon)
 *   • last completed + expires_at dates if available
 *   • a "Start background check" button if status is `not_started`
 *   • a "Continue check" link to the Yardstik portal if `pending`
 *
 * Drop this anywhere on the talent compliance flow (today: IDVerification).
 */

const STATUS_META = {
  approved:  { label: 'Approved',  color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: ShieldCheck,  tone: 'text-emerald-600' },
  pending:   { label: 'Pending',   color: 'bg-amber-100 text-amber-800 border-amber-200',        icon: Shield,       tone: 'text-amber-600' },
  candidate_created: { label: 'Awaiting your action', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Shield, tone: 'text-amber-600' },
  rejected:  { label: 'Rejected',  color: 'bg-rose-100 text-rose-700 border-rose-200',           icon: ShieldX,      tone: 'text-rose-600' },
  expired:   { label: 'Expired',   color: 'bg-rose-100 text-rose-700 border-rose-200',           icon: ShieldAlert,  tone: 'text-rose-600' },
  not_started: { label: 'Not started', color: 'bg-slate-100 text-slate-700 border-slate-200',    icon: Shield,       tone: 'text-slate-500' },
};

const TalentBackgroundCheckCard = () => {
  const [status, setStatus] = useState(null);
  const [requirement, setRequirement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initiating, setInitiating] = useState(false);

  const fetchStatus = async () => {
    try {
      const [statusRes, reqRes] = await Promise.all([
        api.get('/background-checks/my-status'),
        // Iter-411r — Requirement resolver. Drives whether the
        // initiate button is shown. Silently defaults to
        // "not required" if the endpoint is unavailable, matching
        // the cost-protection principle.
        api.get('/background-checks/my-requirement').catch(() => ({ data: { required: false, requirements: [], explanation: '' } })),
      ]);
      setStatus(statusRes.data);
      setRequirement(reqRes.data);
    } catch (e) {
      // Endpoint may 404 on accounts without a talent_profile yet.
      setStatus({ overall_status: 'not_started' });
      setRequirement({ required: false, requirements: [], explanation: '' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleInitiate = async () => {
    setInitiating(true);
    try {
      const res = await api.post('/background-checks/initiate');
      toast.success('Background check started. Check your email for next steps.');
      if (res.data?.invitation_url) {
        window.open(res.data.invitation_url, '_blank', 'noopener,noreferrer');
      }
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start background check');
    } finally {
      setInitiating(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white border-gray-200 shadow-sm" data-testid="bg-check-card-loading">
        <CardContent className="p-6 flex items-center gap-3 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading background check status…</span>
        </CardContent>
      </Card>
    );
  }

  const overall = (status?.overall_status || 'not_started').toLowerCase();
  const meta = STATUS_META[overall] || STATUS_META.not_started;
  const Icon = meta.icon;
  const completedAt = status?.completed_at ? formatDate(status.completed_at) : null;
  const expiresAt = status?.expires_at ? formatDate(status.expires_at) : null;

  return (
    <Card className="bg-white border-gray-200 shadow-sm" data-testid="bg-check-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-xl text-gray-900 flex items-center gap-2">
              <Icon className={`w-5 h-5 ${meta.tone}`} />
              Background Check
            </CardTitle>
            <CardDescription className="text-gray-600 mt-1">
              Required for selected client engagements. Powered by Yardstik.
            </CardDescription>
          </div>
          <Badge className={`${meta.color} border text-xs px-2 py-1`} data-testid="bg-check-status-badge">
            {meta.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {(completedAt || expiresAt) && (
          <div className="grid grid-cols-2 gap-3 text-sm">
            {completedAt && (
              <div>
                <p className="text-xs text-gray-500">Completed</p>
                <p className="font-medium text-gray-900" data-testid="bg-check-completed-at">{completedAt}</p>
              </div>
            )}
            {expiresAt && (
              <div>
                <p className="text-xs text-gray-500">Expires</p>
                <p className="font-medium text-gray-900" data-testid="bg-check-expires-at">{expiresAt}</p>
              </div>
            )}
          </div>
        )}

        {overall === 'rejected' && (
          <Alert variant="destructive" className="bg-rose-50 border-rose-200">
            <AlertDescription>
              Your last background check did not pass our review. Contact support if you believe this is an error.
            </AlertDescription>
          </Alert>
        )}

        {overall === 'expired' && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertDescription>
              Your background check has expired. Please re-run it to keep your eligibility active.
            </AlertDescription>
          </Alert>
        )}

        {/* Iter-411r — When there is no active client requirement, the
            card renders as informational only. Talent cannot start a
            paid screening on a whim; the backend `/initiate` endpoint
            also refuses (defence in depth). */}
        {requirement && !requirement.required && (overall === 'not_started' || overall === 'expired') && (
          <Alert className="bg-slate-50 border-slate-200" data-testid="bg-check-not-required-notice">
            <AlertDescription className="text-sm text-slate-700">
              No background screening is currently required for your bookings.
              You'll be prompted here automatically if a client or event
              requires one — you do not need to start one on your own.
            </AlertDescription>
          </Alert>
        )}

        {/* Iter-411r — Explain WHY when a screening is required, and
            show which booking triggered it. */}
        {requirement && requirement.required && (overall === 'not_started' || overall === 'expired') && (
          <Alert className="bg-cyan-50 border-cyan-200" data-testid="bg-check-required-notice">
            <AlertDescription className="text-sm text-cyan-900">
              <div className="font-medium">Background screening required for this booking.</div>
              {requirement.requirements && requirement.requirements.length > 0 && (
                <ul className="mt-1 text-xs text-cyan-800 list-disc pl-4">
                  {requirement.requirements.slice(0, 3).map((r) => (
                    <li key={r.booking_id}>
                      {r.event_title || 'Shift'} · {r.venue_name || ''} · {r.shift_date || ''}
                    </li>
                  ))}
                </ul>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Iter-411r — Notify talent when we reused an existing valid
            screening instead of ordering another paid one. */}
        {requirement && requirement.reusable_check && (
          <Alert className="bg-emerald-50 border-emerald-200" data-testid="bg-check-reuse-notice">
            <AlertDescription className="text-sm text-emerald-900">
              You already have a valid background screening on file — no new order is needed.
            </AlertDescription>
          </Alert>
        )}

        {/* Iter-411r — Initiate button visible ONLY when the requirement
            resolver says the talent can initiate. Backend still guards
            the paid order independently. */}
        {(overall === 'not_started' || overall === 'expired') &&
         requirement && requirement.can_initiate && (
          <Button
            onClick={handleInitiate}
            disabled={initiating}
            className="bg-cyan-500 hover:bg-cyan-600 w-full sm:w-auto"
            data-testid="bg-check-start-btn"
          >
            {initiating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</>
            ) : (
              <><ShieldCheck className="w-4 h-4 mr-2" /> Start required background check</>
            )}
          </Button>
        )}

        {overall === 'candidate_created' && status?.invitation_url && (
          <Button
            onClick={() => window.open(status.invitation_url, '_blank', 'noopener,noreferrer')}
            variant="outline"
            className="w-full sm:w-auto"
            data-testid="bg-check-continue-btn"
          >
            <ExternalLink className="w-4 h-4 mr-2" /> Continue check on Yardstik
          </Button>
        )}

        {overall === 'pending' && (
          <p className="text-sm text-amber-700" data-testid="bg-check-pending-copy">
            Your check is in review. We'll notify you here and by email when results are in.
          </p>
        )}

        {overall === 'approved' && (
          <p className="text-sm text-emerald-700" data-testid="bg-check-approved-copy">
            You're cleared to be booked for gigs that require a background check.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default TalentBackgroundCheckCard;
